/**
 * Phase J.3.4 — AddBankAccountModal
 *
 * Customer-facing Stripe SetupIntent flow for saving an ACH bank account
 * for off-session re-use (auto-charge on recurring billing).
 *
 * Mirrors the structure of InvoicePayModal:
 *   1. POST /customer/payment-methods/setup-intent → {client_secret, publishable_key}
 *   2. loadStripe + <Elements mode="setup" ...>
 *   3. stripe.confirmSetup({ elements, confirmParams: { return_url } })
 *      — Stripe handles Financial Connections instant verification.
 *   4. On return, the SetupIntent.succeeded webhook persists the PM
 *      server-side; the customer page refetches to render it.
 */
import React, { useEffect, useRef, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
    Elements,
    PaymentElement,
    useElements,
    useStripe,
} from "@stripe/react-stripe-js";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, AlertTriangle, Landmark, Lock, ShieldCheck } from "lucide-react";
import api, { formatApiError } from "@/lib/api";


function SetupInner({ onInitiated, closeModal }) {
    const stripe = useStripe();
    const elements = useElements();
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [elementReady, setElementReady] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!stripe || !elements || submitting) return;
        setSubmitting(true);
        setError(null);
        try {
            const returnUrl = `${window.location.origin}/customer/payment-methods?setup_return=1`;
            onInitiated && onInitiated();
            // Phase J.3.4 — Option B: let Stripe collect whatever billing
            // address fields its `us_bank_account` flow requires (country,
            // postal_code, line1, city, state — the exact set evolves).
            // We don't pass a payment_method_data block here; Stripe reads
            // every field straight from the Element. This keeps the modal
            // resilient to future Stripe schema changes.
            const result = await stripe.confirmSetup({
                elements,
                confirmParams: { return_url: returnUrl },
            });
            if (result?.error) {
                setError(
                    result.error.message ||
                        "We couldn't save your bank authorization. Please try again."
                );
            }
        } catch (err) {
            setError(err?.message || "Unexpected error saving bank account.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="add-bank-form">
            <div className="rounded-lg border border-slate-200 bg-white p-3">
                <PaymentElement
                    options={{
                        layout: "tabs",
                        paymentMethodOrder: ["us_bank_account"],
                        wallets: { applePay: "never", googlePay: "never" },
                        // Phase J.3.4 — no `fields` overrides. Stripe
                        // surfaces every billing field its current ACH
                        // flow needs. Customer expects this when adding a
                        // bank account.
                    }}
                    onReady={() => setElementReady(true)}
                    onLoadError={(ev) =>
                        setError(
                            ev?.error?.message ||
                                "Stripe Elements failed to load. Refresh and try again."
                        )
                    }
                />
            </div>
            <div className="rounded-md bg-slate-50 ring-1 ring-slate-200 p-3 text-xs text-slate-600 leading-relaxed">
                <p className="flex items-start gap-2">
                    <ShieldCheck size={14} className="mt-0.5 text-emerald-700 shrink-0" />
                    <span>
                        By authorizing, you allow RentFlow and its payments
                        partner Stripe to save the selected bank account
                        for future invoice payments and (when enabled by
                        your admin) automatic recurring charges. You may
                        remove this authorization at any time.
                    </span>
                </p>
            </div>
            {error && (
                <div
                    className="rounded-md bg-red-50 ring-1 ring-red-200 p-3 text-sm text-red-800 flex items-start gap-2"
                    data-testid="add-bank-error"
                >
                    <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                    <span>{error}</span>
                </div>
            )}
            <DialogFooter className="gap-2 pt-1">
                <Button
                    type="button"
                    variant="ghost"
                    onClick={closeModal}
                    disabled={submitting}
                    data-testid="add-bank-cancel"
                >
                    Cancel
                </Button>
                <Button
                    type="submit"
                    disabled={!stripe || !elements || submitting || !elementReady}
                    className="bg-blue-900 hover:bg-blue-800 gap-2 min-w-[180px]"
                    data-testid="add-bank-save"
                >
                    {submitting ? (
                        <>
                            <Loader2 size={14} className="animate-spin" /> Saving…
                        </>
                    ) : (
                        <>
                            <Lock size={14} /> Save bank account
                        </>
                    )}
                </Button>
            </DialogFooter>
        </form>
    );
}


export default function AddBankAccountModal({ open, onOpenChange, onInitiated }) {
    const [boot, setBoot] = useState({
        loading: false, error: null, clientSecret: null, stripePromise: null,
    });
    const fetchedFor = useRef(false);

    useEffect(() => {
        if (!open) return;
        if (fetchedFor.current) return;
        fetchedFor.current = true;
        setBoot({ loading: true, error: null, clientSecret: null, stripePromise: null });
        (async () => {
            try {
                const { data } = await api.post(
                    "/customer/payment-methods/setup-intent"
                );
                if (!data?.client_secret || !data?.publishable_key) {
                    throw new Error("Stripe SetupIntent was not configured. Please contact support.");
                }
                const stripePromise = loadStripe(data.publishable_key);
                setBoot({
                    loading: false, error: null,
                    clientSecret: data.client_secret, stripePromise,
                });
            } catch (err) {
                setBoot({
                    loading: false, error: formatApiError(err),
                    clientSecret: null, stripePromise: null,
                });
            }
        })();
    }, [open]);

    useEffect(() => {
        if (!open) {
            fetchedFor.current = false;
            setBoot({ loading: false, error: null, clientSecret: null, stripePromise: null });
        }
    }, [open]);

    const close = () => onOpenChange && onOpenChange(false);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="sm:max-w-[520px]"
                data-testid="add-bank-modal"
            >
                <DialogHeader>
                    <DialogTitle className="font-display text-xl text-slate-900 flex items-center gap-2">
                        <Landmark size={18} className="text-blue-900" />
                        Add bank account
                    </DialogTitle>
                    <DialogDescription className="text-slate-500">
                        Securely save a US bank account via Stripe for
                        future invoice payments and recurring auto-charge.
                    </DialogDescription>
                </DialogHeader>

                {boot.loading && (
                    <div
                        className="flex items-center gap-2 text-sm text-slate-500 py-8 justify-center"
                        data-testid="add-bank-loading"
                    >
                        <Loader2 size={14} className="animate-spin" />
                        Preparing secure bank-connect flow…
                    </div>
                )}
                {boot.error && (
                    <div
                        className="rounded-md bg-red-50 ring-1 ring-red-200 p-3 text-sm text-red-800 flex items-start gap-2"
                        data-testid="add-bank-boot-error"
                    >
                        <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                        <span>{boot.error}</span>
                    </div>
                )}
                {boot.clientSecret && boot.stripePromise && (
                    <Elements
                        stripe={boot.stripePromise}
                        options={{
                            clientSecret: boot.clientSecret,
                            appearance: {
                                theme: "stripe",
                                variables: {
                                    colorPrimary: "#1e3a8a",
                                    colorText: "#0f172a",
                                    fontFamily:
                                        'Inter, ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif',
                                    borderRadius: "8px",
                                },
                            },
                        }}
                    >
                        <SetupInner
                            onInitiated={onInitiated}
                            closeModal={close}
                        />
                    </Elements>
                )}
            </DialogContent>
        </Dialog>
    );
}
